import type { AddNoteRequest } from '@contracts/note/AddNoteRequest';
import { DriverDeliveryStatus } from '@contracts/order/IssuePermitApplicationResponse';
import { useToast } from '@src/common/hooks/useToast';
import { useNavigateToOrderDetails } from '@src/orders/hooks/useNavigateToOrderDetails';
import { useRefreshOrders } from '@src/orders/hooks/useRefreshOrders';
import type {
  CompletePermitSchemaType,
  DeliverPermitFields,
} from '@src/permits/completePermit/fields/CompletePermitFields';
import { CompleteAction } from '@src/permits/completePermit/fields/CompletePermitFields';
import { getDeliveryErrorTranslationKey } from '@src/permits/services/deliveryErrorTranslationKey';
import { PermitApplicationClient } from '@src/permits/services/PermitApplicationClient';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface Props {
  orderId: string;
  permitApplicationId: string;
  userId: string;
}

export const useCompleteApplication = ({
  orderId,
  permitApplicationId,
}: Props) => {
  const { t } = useTranslation();
  const { navigateToOrderDetails } = useNavigateToOrderDetails();
  const { showSuccessToast, showErrorToast, showWarningToast } = useToast();
  const { refreshOrders } = useRefreshOrders();

  const redirectToOrderDetail = () => {
    refreshOrders();
    void navigateToOrderDetails({ orderId });
  };

  const { mutate: completeApplication } = useMutation({
    mutationFn: async ({
      completePermitForm,
      note,
    }: {
      completePermitForm: CompletePermitSchemaType;
      note?: AddNoteRequest;
    }) => {
      switch (completePermitForm.completionType) {
        case CompleteAction.DELIVER: {
          const {
            completionType: _type,
            documents: _documents,
            statePermitDocument: _statePermitDocument,
            ...rest
          } = completePermitForm;
          return await PermitApplicationClient.issueApplication({
            permitApplicationId,
            completePermitFields: rest as DeliverPermitFields,
          });
        }
        case CompleteAction.DENY:
          if (!note) {
            throw new Error('Note is required when denying permit');
          }
          return await PermitApplicationClient.markAsDenied({
            permitApplicationId,
            completePermitFields: completePermitForm,
            note,
          });
        case CompleteAction.FLAG_ERROR:
          if (!note) {
            throw new Error('Note is required when flagging permit with error');
          }
          return await PermitApplicationClient.issueError({
            permitApplicationId,
            completePermitFields: completePermitForm,
            note,
          });
      }
    },
    onSuccess: (result, { completePermitForm: { completionType } }) => {
      if (result?.driverDelivery?.status === DriverDeliveryStatus.FAILED) {
        const reasonKey = getDeliveryErrorTranslationKey(
          result.driverDelivery.error,
        );
        const reason = reasonKey ? t(reasonKey) : '';
        showWarningToast(
          t('issuePermitMessage.DELIVER.partialSuccess', { reason }),
        );
      } else {
        showSuccessToast(t(`issuePermitMessage.${completionType}.success`));
      }
      redirectToOrderDetail();
    },
    onError: (_, { completePermitForm: { completionType } }) => {
      showErrorToast(t(`issuePermitMessage.${completionType}.error`));
    },
  });

  return { completeApplication };
};
